import { apiGet, apiPost, apiDelete } from './api';

export interface ApprovedContact {
  id: string;
  name: string;
  email: string;
  relation?: string;
  addedAt: string;
}

export async function getApprovedContacts(): Promise<ApprovedContact[]> {
  try {
    const res = await apiGet<any[]>('/api/family-members');
    if (res.success && Array.isArray(res.data)) {
      return res.data.map(item => ({
        id: item.id,
        name: item.name,
        email: item.email || '',
        relation: item.relation || 'سایر',
        addedAt: item.created_at || new Date().toISOString()
      }));
    }
  } catch (err) {
    console.warn('Failed fetching approved contacts from D1:', err);
  }
  return [];
}

export async function verifyUserEmailExists(email: string): Promise<{ exists: boolean; displayName?: string }> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { exists: false };
  }

  try {
    const res: any = await apiGet<any>(`/api/shopping/users/check?email=${encodeURIComponent(cleanEmail)}`);
    if (res.success && res.exists) {
      return { exists: true, displayName: res.displayName };
    }
  } catch (err) {
    console.warn('Error checking user existence:', err);
  }
  return { exists: false };
}

export async function addApprovedContact(
  contactData: { name: string; email: string; relation?: string }
): Promise<{ success: boolean; contacts: ApprovedContact[]; message?: string }> {
  const emailClean = contactData.email.trim().toLowerCase();
  const nameClean = contactData.name.trim();

  if (!emailClean || !emailClean.includes('@')) {
    return { success: false, contacts: await getApprovedContacts(), message: 'آدرس ایمیل معتبر نیست.' };
  }

  // Verify that the user actually exists in the app database
  const userCheck = await verifyUserEmailExists(emailClean);
  if (!userCheck.exists) {
    return { 
      success: false, 
      contacts: await getApprovedContacts(), 
      message: 'کاربری با این آدرس ایمیل در سامانه «پیشرفت» یافت نشد. جهت حفظ امنیت، تنها امکان افزودن کاربران عضو وجود دارد.' 
    };
  }

  const contacts = await getApprovedContacts();
  
  // Check if email already exists in approved contacts list
  const existing = contacts.find(c => c.email.toLowerCase() === emailClean);
  if (existing) {
    return { success: false, contacts, message: 'این ایمیل قبلاً در مخاطبین تاییدشده شما ثبت شده است.' };
  }

  try {
    const res = await apiPost<any>('/api/family-members', {
      name: nameClean || userCheck.displayName || emailClean.split('@')[0],
      email: emailClean,
      relation: contactData.relation || 'سایر'
    });

    const updated = await getApprovedContacts();
    return {
      success: res.success,
      contacts: updated,
      message: res.success ? 'مخاطب تایید و با موفقیت در دیتابیس ابری ذخیره شد.' : (res.error || 'خطا در ذخیره مخاطب.')
    };
  } catch (err: any) {
    return {
      success: false,
      contacts: await getApprovedContacts(),
      message: 'خطا در برقراری ارتباط با سرور.'
    };
  }
}

export async function removeApprovedContact(contactId: string): Promise<ApprovedContact[]> {
  try {
    await apiDelete(`/api/family-members/${contactId}`);
  } catch (err) {
    console.error('Failed deleting contact from D1:', err);
  }
  return await getApprovedContacts();
}
